/**
 * Self-check (Desktop-only, token-driven):
 * - All styles are gated on (min-width: 769px) to leave the mobile branch untouched.
 * - Uses semantic theme tokens for color, spacing, radius, shadow, typography.
 * - No raw hex; glass surfaces via theme.colors.background.liquidGlass(*).
 * - Respects prefers-reduced-motion; all transitions tokenised via theme.motion.
 * - Sticky command bar uses z-index below navbar; modal uses theme.zIndex.modal.
 */

import styled, { css, keyframes } from "styled-components";

const desktop = "@media (min-width: 769px)";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

/* ---------------- Page shell ---------------- */

export const DesktopRoot = styled.div`
  display: none;

  ${desktop} {
    display: block;
    background: ${({ theme }) => theme.colors.background.app};
    color: ${({ theme }) => theme.colors.text.primary};
    padding-bottom: ${({ theme }) => theme.spacing(10)};
  }
`;

export const Container = styled.div`
  ${desktop} {
    width: min(100%, 1240px);
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing(4)};
  }
`;

/* ---------------- Hero ---------------- */

export const Hero = styled.section`
  ${desktop} {
    position: relative;
    margin: ${({ theme }) => theme.spacing(3)} auto ${({ theme }) => theme.spacing(5)};
    width: min(100%, 1320px);
    padding: 0 ${({ theme }) => theme.spacing(4)};
  }
`;

export const HeroFrame = styled.div`
  ${desktop} {
    position: relative;
    overflow: hidden;
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    min-height: 540px;
    isolation: isolate;
    box-shadow: ${({ theme }) => theme.colors.shadow.high};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  }
`;

export const HeroVideo = styled.video`
  ${desktop} {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
  }
`;

export const HeroVeil = styled.div`
  ${desktop} {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: ${({ theme }) => theme.colors.state.backdrop};
    opacity: 0.65;
    pointer-events: none;
  }
`;

export const HeroGrain = styled.div`
  ${desktop} {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: transparent;
  }
`;

export const HeroContent = styled.div`
  ${desktop} {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing(3)};
    height: 100%;
    min-height: 540px;
    padding: ${({ theme }) => theme.spacing(6)} ${({ theme }) => theme.spacing(7)};
    max-width: 780px;
  }
`;

export const HeroEyebrow = styled.span`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    align-self: flex-start;
    padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.75)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.inverse};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight};

    svg {
      color: ${({ theme }) => theme.colors.accent.warning};
    }
  }
`;

export const HeroTitle = styled.h1`
  ${desktop} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.inverse};
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: clamp(40px, 5.5vw, 72px);
    line-height: 1.02;
    letter-spacing: -0.02em;
    font-weight: 800;
    text-wrap: balance;
    text-shadow: 0 2px 12px ${({ theme }) => theme.colors.state.scrim};
    em {
      font-style: normal;
      color: ${({ theme }) => theme.colors.accent.primary};
    }
  }
`;

export const HeroLede = styled.p`
  ${desktop} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.inverse};
    opacity: 0.9;
    font-size: clamp(16px, 1.4vw, 20px);
    line-height: 1.55;
    max-width: 560px;
    text-shadow: 0 2px 12px ${({ theme }) => theme.colors.state.scrim};
  }
`;

export const HeroStats = styled.div`
  ${desktop} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const HeroStat = styled.div`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 600;
    box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight};

    strong {
      font-weight: 800;
      color: ${({ theme }) => theme.colors.accent.warning};
      letter-spacing: -0.01em;
    }
  }
`;

export const HeroActions = styled.div`
  ${desktop} {
    display: flex;
    gap: ${({ theme }) => theme.spacing(1.5)};
    flex-wrap: wrap;
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;

export const HeroScrollHint = styled.div`
  ${desktop} {
    position: absolute;
    bottom: ${({ theme }) => theme.spacing(3)};
    right: ${({ theme }) => theme.spacing(4)};
    z-index: 3;
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.75)};
    padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.85;
    animation: ${floatSlow} 3s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

/* ---------------- Command bar ---------------- */

export const CommandBar = styled.div`
  ${desktop} {
    position: sticky;
    top: ${({ theme }) => theme.spacing(2)};
    z-index: ${({ theme }) => theme.zIndex.sticky};
    margin: 0 auto ${({ theme }) => theme.spacing(4)};
    width: min(100%, 1240px);
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    box-shadow: ${({ theme }) => theme.colors.shadow.medium},
      ${({ theme }) => theme.colors.background.liquidGlassHighlight};
    backdrop-filter: blur(28px) saturate(180%) brightness(1.02);
    -webkit-backdrop-filter: blur(28px) saturate(180%) brightness(1.02);
  }
`;

export const CommandTopRow = styled.div`
  ${desktop} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    gap: ${({ theme }) => theme.spacing(1.5)};
    align-items: center;
  }
`;

export const SearchField = styled.label`
  ${desktop} {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: 0 ${({ theme }) => theme.spacing(2)};
    height: ${({ theme }) => theme.spacing(6.5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    transition: border-color ${({ theme }) => theme.motion.fast} ease,
      box-shadow ${({ theme }) => theme.motion.fast} ease;

    &:focus-within {
      border-color: ${({ theme }) => theme.colors.accent.primary};
      box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.state.selected};
    }

    svg.leading {
      color: ${({ theme }) => theme.colors.text.secondary};
      flex-shrink: 0;
    }
  }
`;

export const SearchInput = styled.input`
  ${desktop} {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    font-family: inherit;

    &::placeholder {
      color: ${({ theme }) => theme.colors.input.placeholder};
    }
  }
`;

export const SearchClear = styled.button`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${({ theme }) => theme.spacing(3.5)};
    height: ${({ theme }) => theme.spacing(3.5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    border: none;
    background: ${({ theme }) => theme.colors.state.hover};
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      background: ${({ theme }) => theme.colors.state.active};
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`;

export const KbdHint = styled.kbd`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    padding: ${({ theme }) => `2px ${theme.spacing(0.75)}`};
    border-radius: ${({ theme }) => `${theme.radius.sm}px`};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 11px;
    font-weight: 700;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.04em;
    line-height: 1.6;
  }
`;

export const SortMenu = styled.div`
  ${desktop} {
    position: relative;
  }
`;

export const SortButton = styled.button<{ $open?: boolean }>`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    height: ${({ theme }) => theme.spacing(6.5)};
    padding: 0 ${({ theme }) => theme.spacing(2)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ $open, theme }) =>
      $open ? theme.colors.state.selected : theme.colors.background.surface};
    color: ${({ theme }) => theme.colors.text.primary};
    border: 1px solid
      ${({ $open, theme }) =>
        $open ? theme.colors.accent.primary : theme.colors.border.subtle};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 600;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      border-color ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      border-color: ${({ theme }) => theme.colors.accent.primary};
    }

    svg {
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  }
`;

export const SortPopover = styled.div`
  ${desktop} {
    position: absolute;
    top: calc(100% + ${({ theme }) => theme.spacing(1)});
    right: 0;
    min-width: 220px;
    padding: ${({ theme }) => theme.spacing(1)};
    border-radius: ${({ theme }) => `${theme.radius.lg}px`};
    background: ${({ theme }) => theme.colors.background.elevated};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    box-shadow: ${({ theme }) => theme.colors.shadow.high};
    display: flex;
    flex-direction: column;
    gap: 2px;
    z-index: ${({ theme }) => theme.zIndex.dropdown};
  }
`;

export const SortItem = styled.button<{ $active?: boolean }>`
  ${desktop} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)}`};
    border: none;
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    background: ${({ $active, theme }) =>
      $active ? theme.colors.state.selected : "transparent"};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.accent.primary : theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 600;
    cursor: pointer;
    text-align: left;
    transition: background ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      background: ${({ theme }) => theme.colors.state.hover};
    }

    svg {
      margin-left: auto;
      color: ${({ theme }) => theme.colors.accent.primary};
    }
  }
`;

export const ViewToggle = styled.div`
  ${desktop} {
    display: inline-flex;
    height: ${({ theme }) => theme.spacing(6.5)};
    padding: 4px;
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  }
`;

export const ViewToggleBtn = styled.button<{ $active?: boolean }>`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing(0.75)};
    padding: 0 ${({ theme }) => theme.spacing(1.75)};
    border: none;
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ $active, theme }) =>
      $active ? theme.colors.background.surface : "transparent"};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.accent.primary : theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease,
      box-shadow ${({ theme }) => theme.motion.fast} ease;
    box-shadow: ${({ $active, theme }) =>
      $active ? theme.colors.shadow.low : "none"};
  }
`;

export const FiltersButton = styled.button<{ $count?: number }>`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    height: ${({ theme }) => theme.spacing(6.5)};
    padding: 0 ${({ theme }) => theme.spacing(2.25)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ $count, theme }) =>
      $count && $count > 0
        ? theme.colors.accent.primary
        : theme.colors.background.surface};
    color: ${({ $count, theme }) =>
      $count && $count > 0
        ? theme.colors.text.inverse
        : theme.colors.text.primary};
    border: 1px solid
      ${({ $count, theme }) =>
        $count && $count > 0
          ? theme.colors.accent.primaryHover
          : theme.colors.border.subtle};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 700;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease,
      border-color ${({ theme }) => theme.motion.fast} ease,
      transform ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: ${({ theme }) => theme.colors.shadow.low};
    }

    span.badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border-radius: 999px;
      background: ${({ $count, theme }) =>
        $count && $count > 0
          ? theme.colors.text.inverse
          : theme.colors.accent.primary};
      color: ${({ $count, theme }) =>
        $count && $count > 0
          ? theme.colors.accent.primary
          : theme.colors.text.inverse};
      font-size: 12px;
      font-weight: 800;
    }
  }
`;

export const ChipRailWrap = styled.div`
  ${desktop} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const ChipRail = styled.div`
  ${desktop} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1)};
    flex: 1;
  }
`;

export const QuickChip = styled.button<{ $active?: boolean; $tone?: "neutral" | "organic" }>`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.75)};
    padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.75)}`};
    min-height: ${({ theme }) => theme.spacing(4.5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease,
      border-color ${({ theme }) => theme.motion.fast} ease,
      transform ${({ theme }) => theme.motion.fast} ease;
    background: ${({ $active, $tone, theme }) =>
      $active
        ? $tone === "organic"
          ? theme.colors.accent.primary
          : theme.colors.state.selected
        : theme.colors.background.surface};
    color: ${({ $active, $tone, theme }) =>
      $active
        ? $tone === "organic"
          ? theme.colors.text.inverse
          : theme.colors.accent.primary
        : theme.colors.text.primary};
    border: 1px solid
      ${({ $active, $tone, theme }) =>
        $active
          ? $tone === "organic"
            ? theme.colors.accent.primaryHover
            : theme.colors.accent.primary
          : theme.colors.border.subtle};

    &:hover {
      transform: translateY(-1px);
      border-color: ${({ theme }) => theme.colors.accent.primary};
    }

    svg {
      font-size: 14px;
    }
  }
`;

export const ActiveSummary = styled.div`
  ${desktop} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    flex-wrap: wrap;
    padding-top: ${({ theme }) => theme.spacing(0.5)};
    border-top: 1px dashed ${({ theme }) => theme.colors.border.subtle};
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const ActiveLabel = styled.span`
  ${desktop} {
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
`;

export const RemovableChip = styled.button`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.5)};
    padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.25)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.state.selected};
    color: ${({ theme }) => theme.colors.accent.primary};
    border: 1px solid ${({ theme }) => theme.colors.accent.primary};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      background: ${({ theme }) => theme.colors.state.active};
    }
  }
`;

export const ResetAll = styled.button`
  ${desktop} {
    margin-left: auto;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.text.link};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

/* ---------------- Results section ---------------- */

export const ResultsHeader = styled.div`
  ${desktop} {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};
    margin: ${({ theme }) => theme.spacing(2)} 0 ${({ theme }) => theme.spacing(3)};
  }
`;

export const SectionTitleGroup = styled.div`
  ${desktop} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const SectionKicker = styled.span`
  ${desktop} {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

export const SectionTitle = styled.h2`
  ${desktop} {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: ${({ theme }) => theme.typography.h2};
    color: ${({ theme }) => theme.colors.text.primary};
    letter-spacing: -0.015em;
    font-weight: 700;
  }
`;

export const ResultsMeta = styled.span`
  ${desktop} {
    font-size: ${({ theme }) => theme.typography.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;

    strong {
      color: ${({ theme }) => theme.colors.text.primary};
      font-weight: 800;
    }
  }
`;

/* ---------------- Card grid / list ---------------- */

export const Grid = styled.div`
  ${desktop} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: ${({ theme }) => theme.spacing(3)};
    align-items: stretch;
  }
`;

export const ListStack = styled.div`
  ${desktop} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;

export const Card = styled.article<{ $variant?: "grid" | "list" }>`
  ${desktop} {
    position: relative;
    display: ${({ $variant }) => ($variant === "list" ? "grid" : "flex")};
    grid-template-columns: ${({ $variant }) =>
      $variant === "list" ? "minmax(280px, 340px) minmax(0, 1fr)" : "none"};
    flex-direction: ${({ $variant }) => ($variant === "list" ? "unset" : "column")};
    overflow: hidden;
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
    cursor: pointer;
    transition: transform ${({ theme }) => theme.motion.base} ease,
      box-shadow ${({ theme }) => theme.motion.base} ease,
      border-color ${({ theme }) => theme.motion.base} ease;
    isolation: isolate;

    &:hover {
      transform: translateY(-6px);
      box-shadow: ${({ theme }) => theme.colors.shadow.high};
      border-color: ${({ theme }) => theme.colors.accent.primary};
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.state.selected},
        ${({ theme }) => theme.colors.shadow.medium};
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
      &:hover {
        transform: none;
      }
    }
  }
`;

export const CardMedia = styled.div<{ $variant?: "grid" | "list" }>`
  ${desktop} {
    position: relative;
    width: 100%;
    aspect-ratio: ${({ $variant }) => ($variant === "list" ? "4 / 3" : "16 / 10")};
    overflow: hidden;
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
  }
`;

export const CardImage = styled.div<{ $src: string }>`
  ${desktop} {
    position: absolute;
    inset: 0;
    background-image: url(${({ $src }) => $src});
    background-size: cover;
    background-position: center;
    transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1),
      filter ${({ theme }) => theme.motion.base} ease;

    ${Card}:hover & {
      transform: scale(1.06);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
      ${Card}:hover & {
        transform: none;
      }
    }
  }
`;

export const CardScrim = styled.div`
  ${desktop} {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      transparent 0%,
      transparent 55%,
      ${({ theme }) => theme.colors.state.scrim} 100%
    );
    pointer-events: none;
    opacity: 0.75;
    transition: opacity ${({ theme }) => theme.motion.base} ease;

    ${Card}:hover & {
      opacity: 0.95;
    }
  }
`;

export const OrganicBadge = styled.div`
  ${desktop} {
    position: absolute;
    top: ${({ theme }) => theme.spacing(1.5)};
    left: ${({ theme }) => theme.spacing(1.5)};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: ${({ theme }) => `4px ${theme.spacing(1.25)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
    z-index: 2;
  }
`;

export const WishlistBtn = styled.button<{ $saved?: boolean }>`
  ${desktop} {
    position: absolute;
    top: ${({ theme }) => theme.spacing(1.5)};
    right: ${({ theme }) => theme.spacing(1.5)};
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    color: ${({ $saved, theme }) =>
      $saved ? theme.colors.accent.danger : theme.colors.text.inverse};
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);
    cursor: pointer;
    transition: transform ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease,
      background ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      transform: scale(1.08);
      color: ${({ theme }) => theme.colors.accent.danger};
      background: ${({ theme }) => theme.colors.background.glass};
    }

    svg {
      font-size: 18px;
    }
  }
`;

export const OwnerBadge = styled.div`
  ${desktop} {
    position: absolute;
    left: ${({ theme }) => theme.spacing(1.5)};
    bottom: ${({ theme }) => theme.spacing(1.5)};
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: 4px ${({ theme }) => theme.spacing(1.25)} 4px 4px;
    max-width: calc(100% - 76px);
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    color: ${({ theme }) => theme.colors.text.inverse};
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);
    box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight};
  }
`;

export const OwnerAvatar = styled.img`
  ${desktop} {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    object-fit: cover;
    flex-shrink: 0;
  }
`;

export const OwnerInitials = styled.div`
  ${desktop} {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: 12px;
    font-weight: 800;
  }
`;

export const OwnerName = styled.span`
  ${desktop} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

export const CardBody = styled.div<{ $variant?: "grid" | "list" }>`
  ${desktop} {
    padding: ${({ theme }) => theme.spacing(2.5)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.25)};
    flex: 1;
  }
`;

export const TagRow = styled.ul`
  ${desktop} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(0.75)};
    padding: 0;
    margin: 0;
    list-style: none;
  }
`;

export const Tag = styled.li`
  ${desktop} {
    padding: ${({ theme }) => `2px ${theme.spacing(1)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.state.hover};
    color: ${({ theme }) => theme.colors.accent.primary};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border: 1px solid transparent;
  }
`;

export const CardTitle = styled.h3`
  ${desktop} {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: clamp(20px, 1.5vw, 24px);
    font-weight: 700;
    letter-spacing: -0.015em;
    color: ${({ theme }) => theme.colors.text.primary};
    line-height: 1.2;
  }
`;

export const MetaRow = styled.div`
  ${desktop} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1.25)};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 600;
  }
`;

export const MetaItem = styled.span`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    svg {
      color: ${({ theme }) => theme.colors.accent.primary};
      flex-shrink: 0;
    }
  }
`;

export const CardDescription = styled.p`
  ${desktop} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.body};
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const CardActions = styled.div`
  ${desktop} {
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
    margin-top: auto;
    padding-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const CardAccent = styled.div`
  ${desktop} {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 4px;
    background: ${({ theme }) => theme.colors.accent.primary};
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity ${({ theme }) => theme.motion.base} ease;

    ${Card}:hover & {
      opacity: 1;
    }
  }
`;

/* ---------------- Skeletons ---------------- */

const skeletonBg = css`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background.surfaceAlt} 0%,
    ${({ theme }) => theme.colors.background.surface} 50%,
    ${({ theme }) => theme.colors.background.surfaceAlt} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s infinite linear;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SkeletonCard = styled.div`
  ${desktop} {
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    overflow: hidden;
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  }
`;

export const SkeletonMedia = styled.div`
  ${desktop} {
    width: 100%;
    aspect-ratio: 16 / 10;
    ${skeletonBg}
  }
`;

export const SkeletonBody = styled.div`
  ${desktop} {
    padding: ${({ theme }) => theme.spacing(2.5)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.25)};
  }
`;

export const SkeletonLine = styled.div<{ $width?: number; $height?: number }>`
  ${desktop} {
    height: ${({ $height = 12 }) => `${$height}px`};
    width: ${({ $width = 100 }) => `${$width}%`};
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    ${skeletonBg}
  }
`;

export const SkeletonTag = styled.div`
  ${desktop} {
    width: 60px;
    height: 18px;
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    ${skeletonBg}
  }
`;

export const SkeletonTagRow = styled.div`
  ${desktop} {
    display: flex;
    gap: ${({ theme }) => theme.spacing(0.75)};
  }
`;

/* ---------------- Empty state ---------------- */

export const EmptyState = styled.div`
  ${desktop} {
    grid-column: 1 / -1;
    text-align: center;
    padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(4)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px dashed ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
  }
`;

export const EmptyIcon = styled.div`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.state.hover};
    color: ${({ theme }) => theme.colors.accent.primary};
    font-size: 32px;
  }
`;

export const EmptyTitle = styled.h3`
  ${desktop} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h3};
    font-family: ${({ theme }) => theme.typography.headingFamily};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const EmptyCopy = styled.p`
  ${desktop} {
    margin: 0;
    max-width: 420px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.body};
  }
`;

export const ErrorBanner = styled.div`
  ${desktop} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
    padding: ${({ theme }) => theme.spacing(1.5)}
      ${({ theme }) => theme.spacing(2)};
    border-radius: ${({ theme }) => `${theme.radius.lg}px`};
    background: ${({ theme }) => theme.colors.state.hover};
    color: ${({ theme }) => theme.colors.text.primary};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    margin-bottom: ${({ theme }) => theme.spacing(3)};
    font-size: ${({ theme }) => theme.typography.body};

    svg {
      color: ${({ theme }) => theme.colors.accent.warning};
      flex-shrink: 0;
    }
  }
`;

/* ---------------- Features section ---------------- */

export const FeaturesSection = styled.section`
  ${desktop} {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

export const FeaturesHeader = styled.div`
  ${desktop} {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }
`;

export const FeaturesGrid = styled.div`
  ${desktop} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing(3)};
  }
`;

export const FeatureCard = styled.div<{ $accent: "green" | "yellow" | "blue" }>`
  ${desktop} {
    position: relative;
    padding: ${({ theme }) => theme.spacing(4)};
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
    transition: transform ${({ theme }) => theme.motion.base} ease,
      box-shadow ${({ theme }) => theme.motion.base} ease,
      border-color ${({ theme }) => theme.motion.base} ease;
    overflow: hidden;

    &:hover {
      transform: translateY(-6px);
      box-shadow: ${({ theme }) => theme.colors.shadow.medium};
      border-color: ${({ $accent, theme }) =>
        $accent === "yellow"
          ? theme.colors.accent.warning
          : $accent === "blue"
          ? theme.colors.accent.info
          : theme.colors.accent.primary};
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: -40px;
      width: 180px;
      height: 180px;
      border-radius: 999px;
      background: ${({ $accent, theme }) =>
        $accent === "yellow"
          ? theme.colors.accent.warning
          : $accent === "blue"
          ? theme.colors.accent.info
          : theme.colors.accent.primary};
      opacity: 0.08;
      transform: translate(30%, -30%);
      transition: opacity ${({ theme }) => theme.motion.base} ease,
        transform ${({ theme }) => theme.motion.base} ease;
      pointer-events: none;
    }

    &:hover::after {
      opacity: 0.18;
      transform: translate(20%, -20%);
    }
  }
`;

export const FeatureIconTile = styled.div<{ $accent: "green" | "yellow" | "blue" }>`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: ${({ theme }) => `${theme.radius.lg}px`};
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    background: ${({ $accent, theme }) =>
      $accent === "yellow"
        ? theme.colors.accent.warning
        : $accent === "blue"
        ? theme.colors.accent.info
        : theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: 24px;
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  }
`;

export const FeatureTitle = styled.h3`
  ${desktop} {
    margin: 0 0 ${({ theme }) => theme.spacing(1)} 0;
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: ${({ theme }) => theme.typography.h3};
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const FeatureText = styled.p`
  ${desktop} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.body};
    line-height: 1.6;
  }
`;

/* ---------------- Newsletter ---------------- */

export const NewsletterSection = styled.section`
  ${desktop} {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

export const NewsletterPanel = styled.div`
  ${desktop} {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(5)};
    align-items: center;
    padding: ${({ theme }) => theme.spacing(6)};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -80px;
      right: -80px;
      width: 300px;
      height: 300px;
      border-radius: 999px;
      background: ${({ theme }) => theme.colors.accent.primary};
      opacity: 0.08;
      pointer-events: none;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -60px;
      left: -60px;
      width: 220px;
      height: 220px;
      border-radius: 999px;
      background: ${({ theme }) => theme.colors.accent.warning};
      opacity: 0.1;
      pointer-events: none;
    }
  }
`;

export const NewsletterCopy = styled.div`
  ${desktop} {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const NewsletterTitle = styled.h2`
  ${desktop} {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: ${({ theme }) => theme.typography.h2};
    letter-spacing: -0.015em;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const NewsletterBody = styled.p`
  ${desktop} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.6;
    max-width: 440px;
  }
`;

export const NewsletterPerks = styled.ul`
  ${desktop} {
    margin: ${({ theme }) => theme.spacing(1)} 0 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.75)};
    list-style: none;
  }
`;

export const NewsletterPerk = styled.li`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    font-size: ${({ theme }) => theme.typography.body};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;

    svg {
      color: ${({ theme }) => theme.colors.accent.primary};
      flex-shrink: 0;
    }
  }
`;

export const NewsletterForm = styled.form`
  ${desktop} {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.25)};
    padding: ${({ theme }) => theme.spacing(3)};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    backdrop-filter: blur(28px) saturate(180%);
    -webkit-backdrop-filter: blur(28px) saturate(180%);
    box-shadow: ${({ theme }) => theme.colors.shadow.medium},
      ${({ theme }) => theme.colors.background.liquidGlassHighlight};
  }
`;

export const NewsletterField = styled.label`
  ${desktop} {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: 0 ${({ theme }) => theme.spacing(2)};
    height: ${({ theme }) => theme.spacing(7)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    transition: border-color ${({ theme }) => theme.motion.fast} ease,
      box-shadow ${({ theme }) => theme.motion.fast} ease;

    &:focus-within {
      border-color: ${({ theme }) => theme.colors.accent.primary};
      box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.state.selected};
    }

    svg {
      color: ${({ theme }) => theme.colors.text.secondary};
      flex-shrink: 0;
    }
  }
`;

export const NewsletterInput = styled.input`
  ${desktop} {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    font-family: inherit;

    &::placeholder {
      color: ${({ theme }) => theme.colors.input.placeholder};
    }
  }
`;

export const NewsletterNote = styled.p`
  ${desktop} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-align: center;
  }
`;

/* ---------------- Modal ---------------- */

export const ModalOverlay = styled.div`
  ${desktop} {
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndex.modalOverlay};
    background: ${({ theme }) => theme.colors.state.backdrop};
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

export const ModalFrame = styled.div`
  ${desktop} {
    position: relative;
    width: min(100%, 820px);
    max-height: 90vh;
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    border-radius: ${({ theme }) => `${theme.radius.xl}px`};
    box-shadow: ${({ theme }) => theme.colors.shadow.high};
    overflow: hidden;
    display: grid;
    grid-template-rows: 320px auto;
  }
`;

export const ModalMedia = styled.div`
  ${desktop} {
    position: relative;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
  }
`;

export const ModalImage = styled.img`
  ${desktop} {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const ModalMediaScrim = styled.div`
  ${desktop} {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      transparent 40%,
      ${({ theme }) => theme.colors.state.scrim} 100%
    );
    pointer-events: none;
  }
`;

export const ModalClose = styled.button`
  ${desktop} {
    position: absolute;
    top: ${({ theme }) => theme.spacing(2)};
    right: ${({ theme }) => theme.spacing(2)};
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    color: ${({ theme }) => theme.colors.text.inverse};
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);
    cursor: pointer;
    font-size: 20px;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      transform ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      background: ${({ theme }) => theme.colors.background.glass};
      transform: scale(1.06);
    }
  }
`;

export const ModalTitleBlock = styled.div`
  ${desktop} {
    position: absolute;
    left: ${({ theme }) => theme.spacing(4)};
    right: ${({ theme }) => theme.spacing(4)};
    bottom: ${({ theme }) => theme.spacing(3)};
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.75)};
  }
`;

export const ModalKicker = styled.div`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.75)};
    align-self: flex-start;
    padding: ${({ theme }) => `4px ${theme.spacing(1)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    backdrop-filter: blur(18px) saturate(170%);
    -webkit-backdrop-filter: blur(18px) saturate(170%);
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`;

export const ModalTitle = styled.h2`
  ${desktop} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.inverse};
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: clamp(28px, 3vw, 40px);
    font-weight: 800;
    letter-spacing: -0.02em;
    text-shadow: 0 6px 28px ${({ theme }) => theme.colors.state.scrim};`;

export const ModalBody = styled.div`
  ${desktop} {
    padding: ${({ theme }) => theme.spacing(4)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    overflow-y: auto;
  }
`;

export const ModalMeta = styled.div`
  ${desktop} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1.5)};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 600;

    svg {
      color: ${({ theme }) => theme.colors.accent.primary};
    }
  }
`;

export const ModalDescription = styled.p`
  ${desktop} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    line-height: 1.7;
  }
`;

export const ModalActions = styled.div`
  ${desktop} {
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
    padding-top: ${({ theme }) => theme.spacing(1)};

    > * {
      flex: 1;
    }
  }
`;

/* ---------------- Desktop filter drawer ---------------- */

export const DrawerOverlay = styled.div`
  ${desktop} {
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndex.modalOverlay};
    background: ${({ theme }) => theme.colors.state.backdrop};
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }
`;

export const DrawerPanel = styled.aside`
  ${desktop} {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: min(440px, 90vw);
    background: ${({ theme }) => theme.colors.background.surface};
    border-left: 1px solid ${({ theme }) => theme.colors.border.subtle};
    box-shadow: ${({ theme }) => theme.colors.shadow.high};
    z-index: ${({ theme }) => theme.zIndex.modal};
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

export const DrawerHeader = styled.header`
  ${desktop} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
  }
`;

export const DrawerTitle = styled.h2`
  ${desktop} {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-size: ${({ theme }) => theme.typography.h3};
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const DrawerSubtitle = styled.p`
  ${desktop} {
    margin: 4px 0 0;
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const DrawerClose = styled.button`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    background: ${({ theme }) => theme.colors.background.surface};
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    font-size: 18px;
    flex-shrink: 0;
    transition: background ${({ theme }) => theme.motion.fast} ease,
      border-color ${({ theme }) => theme.motion.fast} ease;

    &:hover {
      background: ${({ theme }) => theme.colors.state.hover};
      border-color: ${({ theme }) => theme.colors.accent.primary};
    }
  }
`;

export const DrawerBody = styled.div`
  ${desktop} {
    flex: 1;
    overflow-y: auto;
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3.5)};
  }
`;

export const DrawerFieldset = styled.fieldset`
  ${desktop} {
    border: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const DrawerLegend = styled.legend`
  ${desktop} {
    padding: 0;
    margin: 0;
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 800;
    letter-spacing: 0.02em;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const DrawerLegendHint = styled.span`
  ${desktop} {
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    margin-left: ${({ theme }) => theme.spacing(1)};
  }
`;

export const DrawerChipGroup = styled.div`
  ${desktop} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const SwitchRow = styled.label`
  ${desktop} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
    border-radius: ${({ theme }) => `${theme.radius.lg}px`};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    cursor: pointer;
  }
`;

export const SwitchLabel = styled.span`
  ${desktop} {
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
      font-size: ${({ theme }) => theme.typography.body};
      color: ${({ theme }) => theme.colors.text.primary};
      font-weight: 700;
    }

    span {
      font-size: ${({ theme }) => theme.typography.small};
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  }
`;

export const Switch = styled.input`
  ${desktop} {
    appearance: none;
    width: 48px;
    height: 28px;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.border.subtle};
    position: relative;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease;
    flex-shrink: 0;

    &:before {
      content: "";
      position: absolute;
      top: 3px;
      left: 3px;
      width: 22px;
      height: 22px;
      border-radius: 999px;
      background: ${({ theme }) => theme.colors.background.surface};
      box-shadow: ${({ theme }) => theme.colors.shadow.low};
      transition: transform ${({ theme }) => theme.motion.fast} ease;
    }

    &:checked {
      background: ${({ theme }) => theme.colors.accent.primary};
    }

    &:checked:before {
      transform: translateX(20px);
    }
  }
`;

export const DrawerFooter = styled.footer`
  ${desktop} {
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => theme.spacing(3)};
    border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
    background: ${({ theme }) => theme.colors.background.surface};

    > * {
      flex: 1;
    }
  }
`;

/* ---------------- Load more ---------------- */

export const LoadMoreWrap = styled.div`
  ${desktop} {
    display: flex;
    justify-content: center;
    margin-top: ${({ theme }) => theme.spacing(5)};
  }
`;

export const LoadMoreButton = styled.button`
  ${desktop} {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3.5)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.surface};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 700;
    cursor: pointer;
    transition: transform ${({ theme }) => theme.motion.fast} ease,
      background ${({ theme }) => theme.motion.fast} ease,
      border-color ${({ theme }) => theme.motion.fast} ease,
      box-shadow ${({ theme }) => theme.motion.fast} ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      border-color: ${({ theme }) => theme.colors.accent.primary};
      box-shadow: ${({ theme }) => theme.colors.shadow.low};
    }

    &:disabled {
      cursor: wait;
      opacity: 0.6;
    }
  }
`;

/** Says a farm has no reviews yet, rather than showing empty stars that read as a zero score. */
export const MetaMuted = styled.span`
  ${desktop} {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-style: italic;
  }
`;
